import type { Uebersetzer } from "../i18n";
import { kurzDatum } from "../daten/kalender";
import type { Preisauskunft, Preisschritt } from "../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Die Sätze unter einem Preis — an beiden Orten dieselben.

   Das Nachschlagen am Schirm (`masken/Nachschlagen.tsx`, seit dem
   17.09.2026 auch die Preisauskunft) und der Preis am Telefon
   (`masken/NachschlagenTelefon.tsx`) beantworten dieselbe Frage und
   dürfen sie nicht verschieden beantworten: „8 Kalendertage, 6
   Miettage — Samstag und Sonntag zählen nicht" ist ein Satz, kein
   Gestaltungsmittel. Stünde er zweimal im Code, liefe er beim nächsten
   Mietkalender auseinander.

   **Gezählt wird hier nichts.** Die Zahlen und die Gründe kommen aus der
   Preisfindung (`miettageErklaert`) als Schlüssel mit Werten; hier wird
   nur zusammengesetzt.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Die Einheit eines Tarifs als Wort. Die Werte sind die der Datenbank
 * (`rate.unit`: tag · woche · monat · stunde · pauschale) und bleiben
 * deutsch, weil sie Fachbegriffe sind — sichtbar werden sie trotzdem nur
 * über einen Schlüssel. Alle fünf stehen unter `preis.*`.
 */
export const EINHEITSWORT: Record<string, string> = {
  tag: "preis.einheitTag",
  stunde: "preis.einheitStunde",
  pauschale: "preis.einheitPauschale",
  woche: "preis.einheitWoche",
  monat: "preis.einheitMonat",
};

/** Ein Grund als Satzteil. Beim Sechstagekalender fällt nur der Sonntag weg,
 *  und „Samstag und Sonntag zählen nicht" wäre dort falsch. */
export function grundText(t: Uebersetzer, sch: Preisschritt): string {
  if (sch.key === "preis.wochenende_faellt_weg" && sch["kalendertage"] === 6) {
    return t("preis.sonntag_faellt_weg");
  }
  return t(sch.key, sch as never);
}

/**
 * Was diese Zeile vom Preis braucht — beide Formen liefern es.
 *
 * `Preisauskunft` (`GET /preis`) trägt Ende und Kalendertage verschachtelt,
 * `Buendelpreis` (`GET /preise`) flach. Statt zweier Fassungen desselben
 * Satzes nimmt er die drei Zahlen, um die es geht — wer ihn ruft, gleicht an.
 */
export type Zeitangabe = {
  miettage: number;
  /** Das gerechnete Ende. */
  bis: string;
  /** Wie viele Kalendertage das sind, beide Enden eingeschlossen. */
  kalendertage: number;
  /** Warum aus den Kalendertagen so viele Miettage werden. Leer heißt: gar nicht. */
  miettageSchritte?: Preisschritt[] | undefined;
};

/**
 * Die Zeile unter dem Preis: bis wann, wie viele Kalendertage, wie viele
 * Miettage — und warum die beiden auseinanderfallen.
 *
 * Birgit, 16.09.2026, vor „8 Tage · 6 Miettage": *„Ich bin bei 8 Tagen, und
 * da stehen 6."* Gerechnet war richtig, nur sagte die Karte nicht, warum.
 */
export function zeitraumZeile(t: Uebersetzer, p: Zeitangabe): string {
  const bisTag = p.bis;
  const kalender = p.kalendertage;
  const d = new Date(`${bisTag}T12:00:00`);
  const wochentag = t.liste("preisauskunft.wochentage")[(d.getDay() + 6) % 7] ?? "";
  const bis = t("kalender.tagMitDatum", { tag: wochentag, datum: kurzDatum(d) });
  const kalendertage = kalender === 1
    ? t("preisauskunft.einKalendertag") : t("preisauskunft.kalendertage", { zahl: kalender });
  const miettage = p.miettage === 1
    ? t("preisauskunft.einMiettag") : t("preisauskunft.miettage", { zahl: p.miettage });

  const gruende = (p.miettageSchritte ?? [])
    .filter((sch) => sch.key !== "preis.miettage")
    .map((sch) => grundText(t, sch));
  if (gruende.length) {
    return t("preisauskunft.zeitraumAnders", {
      bis, kalendertage, miettage, grund: gruende.join(t("preisauskunft.grundTrenner")),
    });
  }
  return kalender === p.miettage
    ? t("preisauskunft.zeitraumGleich", { bis, miettage })
    : t("preisauskunft.zeitraumZahlen", { bis, kalendertage, miettage });
}

/** „Mo 21.09." — Wochentag und Tag, ohne Jahr. */
export function tagMitWochentag(t: Uebersetzer, iso: string): string {
  if (!iso) return t("allgemein.strich");
  const d = new Date(`${iso}T12:00:00`);
  const wochentag = t.liste("preisauskunft.wochentage")[(d.getDay() + 6) % 7] ?? "";
  return t("kalender.tagMitDatum", { tag: wochentag, datum: kurzDatum(d) });
}

/**
 * Die Zeile über einer Auskunft nach **Dauer**: „ab Mo 21.09. · 5 Miettage ·
 * bis Fr 25.09."
 *
 * Luca, 17.09.2026: *„Thema Preisauskunft ist noch nicht perfekt, denn der
 * berücksichtigt die Tage, also Wochenenden, nicht."* Seit dem Ende über den
 * Mietkalender läuft, ist die Frage eine andere: Nicht mehr „warum sind aus
 * acht Tagen sechs geworden", sondern „bis wann reichen meine fünf". Also
 * steht das Enddatum hinten und nicht mehr vorn — und der Grund daneben, wenn
 * dabei Tage übersprungen werden.
 */
export function zeitraumAbBis(t: Uebersetzer, p: Preisauskunft, vonGefragt: string): string {
  const von = tagMitWochentag(t, p.zeitraum?.von ?? vonGefragt);
  const bis = tagMitWochentag(t, p.zeitraum?.bis ?? vonGefragt);
  const miettage = p.miettage === 1
    ? t("preisauskunft.einMiettag") : t("preisauskunft.miettage", { zahl: p.miettage });

  const gruende = (p.miettageSchritte ?? [])
    .filter((sch) => sch.key !== "preis.miettage")
    .map((sch) => grundText(t, sch));
  return gruende.length
    ? t("preisauskunft.abBisGrund", {
      von, miettage, bis, grund: gruende.join(t("preisauskunft.grundTrenner")),
    })
    : t("preisauskunft.abBis", { von, miettage, bis });
}
